import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd, removeWillChangeOnAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element (contains only the hamburger summary).
 * @property {HTMLDialogElement} menuDrawer - The dialog element for the sliding drawer panel.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details', 'menuDrawer'];

  // Close the mobile drawer when switching to a desktop-width viewport (e.g. DevTools resize).
  #desktopMediaQuery = globalThis.matchMedia('(min-width: 750px)');

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.addEventListener('keydown', this.#onEscapeDown);
    this.#setupAnimatedElementListeners();

    this.refs.menuDrawer.addEventListener('close', this.#onDialogClose);

    // Close the drawer if the viewport changes to desktop width.
    this.#desktopMediaQuery.addEventListener('change', this.#onViewportChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
    this.removeEventListener('keydown', this.#onEscapeDown);
    this.#desktopMediaQuery.removeEventListener('change', this.#onViewportChange);
  }

  /** @param {MediaQueryListEvent} event */
  #onViewportChange = (event) => {
    if (event.matches && this.isOpen) {
      this.close();
    }
  };

  /**
   * Prevent Escape keydown default so the browser does not dispatch a cancel event.
   * This keeps the dialog open so #onKeyUp can run the slide-out animation instead.
   * @param {KeyboardEvent} event
   */
  #onEscapeDown = (event) => {
    if (event.key === 'Escape' && this.isOpen) {
      event.preventDefault();
    }
  };

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  #onDialogClose = () => {
    // Dialog closed by any means — fully clean up all state.
    // reset() removes menu-open + the native 'open' attr from details, which fires the toggle
    // event in dialog.js that removes html[scroll-lock]. For intentional closes, #close() already
    // called reset() so this is a no-op.
    this.refs.menuDrawer.classList.remove('menu-open');
    reset(this.refs.details);
    removeTrapFocus();
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.menuDrawer.open;
  }

  /**
   * Get the closest details element to the event target.
   * Falls back to this.refs.details for events inside the dialog (not inside a submenu details).
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {string} [target]
   * @param {Event} [event]
   */
  open(target, event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'true');

    const isMainDrawer = details === this.refs.details;

    if (isMainDrawer) {
      this.refs.menuDrawer.show();
    }

    this.preventInitialAccordionAnimations(details);
    requestAnimationFrame(() => {
      if (isMainDrawer && !this.refs.menuDrawer.open) return;
      details.classList.add('menu-open');

      if (isMainDrawer) {
        this.refs.menuDrawer.classList.add('menu-open');
      }

      if (target) {
        this.refs.menuDrawer.classList.add('menu-drawer--has-submenu-opened');
      }

      // For the main drawer animate the dialog itself; for submenus use the submenu panel.
      const drawer = isMainDrawer
        ? this.refs.menuDrawer
        : details.querySelector('.menu-drawer__submenu');
      const focusTarget = isMainDrawer ? this.refs.menuDrawer : details;

      onAnimationEnd(drawer || details, () => trapFocus(focusTarget), { subtree: false });
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');

    const isMainDrawer = details === this.refs.details;

    if (isMainDrawer) {
      this.refs.menuDrawer.classList.remove('menu-open');
    }

    this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');

    // For the main drawer watch the dialog itself; for submenus watch the submenu panel.
    const drawer = isMainDrawer
      ? this.refs.menuDrawer
      : details.querySelector('.menu-drawer, .menu-drawer__submenu');

    onAnimationEnd(
      drawer || details,
      () => {
        reset(details);
        if (isMainDrawer) {
          this.refs.menuDrawer.close();
          removeTrapFocus();
          const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
          openDetails.forEach(reset);
        } else {
          trapFocus(this.refs.menuDrawer);
        }
      },
      { subtree: false }
    );
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
